import { DateTime } from 'luxon';
import { isEqual, isPlainObject } from 'lodash';

export const DATE_FORMAT = 'yyyy.MM.dd.';
export const YEAR_MONTH_DATE_FORMAT = 'yyyy/MM';
export const TIME_FORMAT = 'HH:mm';
export const TIME_FORMAT_WITH_SECONDS = 'HH:mm:ss';
export const DATE_TIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}`;
export const DATE_TIME_FORMAT_WITH_SECONDS = `${DATE_FORMAT} ${TIME_FORMAT_WITH_SECONDS}`;
export const DATE_MASK = '____.__.__';
export const YEAR_MONTH_DATE_MASK = '____/__';

export const INVALID_DATE = 'Invalid Date';
export const INVALID_DATE_TIME = DateTime.invalid(INVALID_DATE);

export type InputDateType = DateTime;

export interface IDays {
  value: number;
  unit: string;
}

export const plusRelativeDays: IDays = { value: +10, unit: 'day' };
export const minusRelativeDays: IDays = { value: -10, unit: 'day' };
export const relativeToday: IDays = { value: 0, unit: 'day' };

interface Options {
  utc?: boolean;
}

export function formatDate(
  date?: InputDateType | string | null,
  formatString: string = DATE_FORMAT,
  { utc }: Options = { utc: false }
) {
  const newDate = typeof date === 'string' ? DateTime.fromISO(date) : date;

  if (!newDate || !isValidDate(newDate)) {
    return '';
  }

  const zonedTime = utc ? newDate.toUTC() : newDate;

  return zonedTime.toFormat(formatString);
}

export function getRawDateValue(value?: InputDateType | null) {
  if (isValidDate(value)) {
    return value?.toJSDate() || '';
  }

  return '';
}

export function isValidDate(value?: InputDateType | null) {
  if (!value) {
    return true;
  }

  return value.isValid || false;
}

export function toUtcIsoString(date?: DateTime | null) {
  if (!isValidDate(date)) {
    return '';
  }
  return date?.toUTC()?.toISO() || '';
}

export const isDateTime = (value: unknown): value is DateTime =>
  DateTime.isDateTime(value) || value instanceof DateTime;

/**
 * Normalizes a form value (ISO string, relative-date descriptor, DateTime or empty)
 * into a DateTime. Invalid input resolves to an invalid DateTime instead of null, so
 * the picker doesn't reset its section state and can still report an empty field.
 */
export const toDateTime = (raw: unknown): DateTime | null => {
  if (raw == null || raw === '') return null;

  if (isDateTime(raw)) return raw.isValid ? raw : INVALID_DATE_TIME;

  if (isPlainObject(raw)) {
    const now = DateTime.now();
    if (isEqual(raw, plusRelativeDays)) return now.plus({ days: plusRelativeDays.value });
    if (isEqual(raw, minusRelativeDays)) return now.plus({ days: minusRelativeDays.value });
    if (isEqual(raw, relativeToday)) return now;
    return null;
  }

  if (typeof raw === 'string') {
    const parsed = DateTime.fromISO(raw);
    return parsed.isValid ? parsed : INVALID_DATE_TIME;
  }

  return null;
};
